import {getResolvedClient} from './resolver';

import {highlightExistingAction} from '../../core/actionHighlighter';

// The dashboard header names its icons by action key and hides the readable
// text in a Tooltip that never reaches the element, so the aria-label is what
// there is to match on. The same shared header carries this button on the
// Home, Site, Switch, Client and Access Point dashboards.
const ACTION_LABELS = {
	download_report: ['download', 'Download Dashboard Report (PDF)'],
};

/**
 * Build the Client Dashboard path for a navigation command.
 *
 * The route carries the client in the query string as a base64 MAC -
 * `/client?clientId=<encoded>` - which is what a row click builds too. A
 * client has no other unique handle: a username can belong to several
 * connections at once, so the MAC is the only thing that opens one page.
 *
 * @param {object} command navigation command
 * @return {string} router path
 */
export function buildClientDashboardPath(command) {
	const mac = command.params?.entity_id ?? command.params?.client_mac;
	if (!mac) throw new Error('resolved_client_not_available');
	return `/client?clientId=${encodeURIComponent(btoa(String(mac)))}`;
}

/**
 * Build Canvas router state for the Client Dashboard.
 *
 * The page reads the row it was handed rather than fetching it again, which
 * is how a row click leaves it - the row goes into the store under
 * SET_CLIENT_DASHBOARD before the navigation happens.
 *
 * @param {object} command navigation command
 * @return {object|null} router state
 */
export function buildClientNavigationState(command) {
	const mac = command.params?.entity_id ?? command.params?.client_mac;
	return getResolvedClient(mac) || null;
}

/**
 * Verify that the requested Client Dashboard mounted.
 *
 * Compared on the MAC with separators stripped, because Canvas writes it
 * three ways - colons, hyphens, and bare - depending on where the value
 * came from.
 *
 * @param {object} command navigation command
 * @param {object} context registered page context
 * @return {boolean} readiness
 */
export function isClientNavigationReady(command, context) {
	const params = command.params || {};
	const requested = params.entity_id ?? params.client_mac;
	if (!context?.client_id) return false;
	if (requested && bareMac(context.client_id) !== bareMac(requested)) {
		return false;
	}
	const actionTarget = params.action_target;
	if (actionTarget && ACTION_LABELS[actionTarget]) {
		const shown = highlightExistingAction(
			ACTION_LABELS[actionTarget],
			[],
			{
				activate: params.action_activation === 'auto',
				commandId: command.command_id,
			},
		);
		// The Client arrived; only its control is missing.
		return shown ? true : {ready: false, reason: 'action_control_unavailable'};
	}
	return true;
}

/**
 * @param {*} value a MAC in any of the formats Canvas writes
 * @return {string} comparable MAC
 */
function bareMac(value) {
	return String(value || '').replace(/[^a-f0-9]/gi, '').toLowerCase();
}
